import dayjs from 'dayjs';

export const stripHtml = (html: string): string => {
  return html?.replace(/(<([^>]+)>)/gi, '');
};

export const isInViewport = (el: HTMLElement): boolean => {
  const rect = el.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const scrollIntoView = (element: HTMLElement, offset: number = 0): void => {
  if (element) {
    element.scrollIntoView();
    window.scrollTo({
      top: element.offsetTop + offset,
      behavior: 'smooth',
    });
  }
};

export const loadLazyImage = (element: HTMLElement | null): void => {
  if (element) {
    element.querySelectorAll<HTMLElement>('*').forEach((item) => {
      const srcset = item.getAttribute('data-srcset');
      const src = item.getAttribute('data-src');

      if (srcset) {
        item.setAttribute('srcset', srcset);
        item.removeAttribute('data-srcset');
      }
      if (src) {
        item.setAttribute('src', src);
        item.removeAttribute('data-src');
      }
    });
    element.classList.add('lazy-loaded');
  }
};

export const splitWords = (str: string): string[] => {
  return str.split(' ');
};

export const isFunction = (value: unknown): boolean =>
  value
    ? Object.prototype.toString.call(value) === '[object Function]' ||
      'function' === typeof value ||
      value instanceof Function
    : false;

export const buildJsContent = (headSectionScripts: string[]): { vanillaScripts: string; scriptSrcs: string[] } => {
  const vanillaScripts = headSectionScripts[0]
    ?.split(/<\/script>/g)
    ?.map((s: string | null) => {
      if (!s || s === '') return null;
      return s.replace(/<script[^>]*>/g, '').replace(/<\/script>/g, '');
    })
    .filter((s: string | null) => s !== null)
    .join('');

  const scriptSrcs: string[] = [];
  headSectionScripts[0]?.split('\n')?.forEach((s: string) => {
    if (s && s !== '') {
      const match = /src\s*=\s*"([^"]+)/g.exec(s);
      if (match) {
        scriptSrcs.push(...match.filter((res) => !res.startsWith('src=')));
      }
    }
  });

  return { vanillaScripts, scriptSrcs };
};

export const getScrollbarWidth = () => {
  // Creating invisible container
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear
  (outer.style as any)['msOverflowStyle'] = 'scrollbar'; // needed for WinJS apps
  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  // Removing temporary elements from the DOM
  outer.parentNode?.removeChild(outer);

  return scrollbarWidth;
};

export const setScrollbarPlaceholder = (size: number) => {
  const selectors = ['.page-container', 'header.main-header', '.ReportNavigationBar', '.scrollbar-placeholder'];

  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      (el as HTMLElement).style.paddingRight = size ? `${size}px` : '';
    });
  });
};

export const addToCalendar = (event: { summary: string; location: string; dtStart: string; dtEnd: string }) => {
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${dayjs(event.dtStart).format('YYYYMMDDTHHmmss')}
DTEND:${dayjs(event.dtEnd).format('YYYYMMDDTHHmmss')}
SUMMARY:${event.summary}
LOCATION:${event.location}
END:VEVENT
END:VCALENDAR`;

  const blob = new Blob([icsContent], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.summary}-${event.location}-${dayjs(event.dtStart).format('MMM DD')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const formatRemainingTime = (targetDate: string | Date): string => {
  const now = new Date();
  const target = new Date(targetDate);

  const diffMs = target.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'Voting closed';
  }

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays >= 30) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months !== 1 ? 's' : ''} remaining`;
  }

  if (diffDays >= 7) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''} remaining`;
  }

  return `${diffDays} day${diffDays !== 1 ? 's' : ''} remaining`;
};
